import { Element } from 'react-scroll';
import { regionLangMap, useGetRegionLabel } from '../share';
import { useReactScrollContext } from 'views/Aide/components/ReactScrollContext';
import FormField from 'components/FormField';
import { FV } from './share';
import ContentEditor, { getLength, value2editorValue } from '../WhatsAppEditor/ContentEditor';
import useSmartField from 'hooks/useSmartField';
import HeaderEditor from '../WhatsAppEditor/HeaderEditor';
import { Input } from 'antd';
import ButtonsField from '../WhatsAppEditor/ButtonsField';
import wait from 'utils/wait';
import styled from '@emotion/styled';
import WhatsAppTemplatePreview from 'components/WhatsAppTemplatePreview';
import { useFormikContext } from 'formik';
import CopyRegionButton from './CopyRegionButton';

const defaultValue: Aide.WhatsAppContent[] = [];

function Region({
  region,
  showCopy,
  onCopy,
}: {
  region: Aide.WhatsAppRegion;
  showCopy: boolean;
  onCopy: () => void;
}) {
  const { t, getRegionLabel } = useGetRegionLabel();

  const { createRegionContextId } = useReactScrollContext();

  const {
    values: {
      regionTemplateMap: { [region]: template },
    },
  } = useFormikContext<FV>();

  const {
    field: { value: translatedValue = defaultValue, onChange: onTranslatedChange },
  } = useSmartField<Required<FV['regionTemplateMap']>['CN']['translateContent']>(
    `regionTemplateMap.${region}.translateContent`,
  );

  return (
    <Element name={createRegionContextId(region)}>
      <h4>
        {t('模版内容')}-{getRegionLabel(region)}
      </h4>
      <Container>
        <section>
          <FormField<Required<FV['regionTemplateMap']>['CN']['header']>
            name={`regionTemplateMap.${region}.header`}
            label={t('页眉')}
            validate={(value) => {
              if (!value || !value.type) {
                return undefined;
              }
              if (value.type === 'text' && !value.text.trim()) {
                return t('department.请输入x', { x: t('phrase.文本') });
              }
              if (value.type !== 'text' && !value.example) {
                return t('请上传示例');
              }
            }}
          >
            {({ field }) => {
              return <HeaderEditor {...field} />;
            }}
          </FormField>
          <FormField<Required<FV['regionTemplateMap']>['CN']['content']>
            name={`regionTemplateMap.${region}.content`}
            required
            label={t('正文')}
            validate={(value) => {
              return wait(100).then(() => {
                const valueLength = getLength(value || []);
                const translatedValueLength = getLength(translatedValue || []);
                if (!valueLength && !translatedValueLength) {
                  return t('请输入内容');
                }
                if (valueLength > 1024 || translatedValueLength > 1024) {
                  return t('marketing.下发内容不可超出x个字', { x: 1024 });
                }
              });
            }}
          >
            {({ field: { value = defaultValue, onChange } }) => {
              return (
                <ContentEditor
                  lang={regionLangMap[region]}
                  value={value}
                  translatedValue={translatedValue}
                  onChange={onChange}
                  onTranslatedChange={onTranslatedChange}
                />
              );
            }}
          </FormField>
          <FormField<Required<FV['regionTemplateMap']>['CN']['footer']>
            name={`regionTemplateMap.${region}.footer`}
            label={t('页脚')}
            description={t(
              '应WhatsApp官方要求，首次触达买家需获取opt-in，该信息在话术页脚固定展示，不可修改。',
            )}
          >
            {({ field }) => {
              return <Input disabled {...field} />;
            }}
          </FormField>
          <ButtonsField region={region} />
        </section>
        <div>
          <div>{t('示例')}</div>
          <section>
            <header>{t('店铺名称')}</header>
            <div>
              <WhatsAppTemplatePreview
                style={{
                  width: '288px',
                }}
                template={{
                  header: template?.header,
                  contents: value2editorValue(
                    (getLength(template?.translateContent || [])
                      ? template?.translateContent
                      : template?.content) || [],
                  ),
                  footer: template?.footer,
                  buttons: template?.buttons,
                }}
                assets={{
                  pdfTitleText: t('ViewsChat.pdf第一页缩略图'),
                  pdfNameText: t('ViewsChat.文件名'),
                  contentPlaceholder: t('ViewsChat.输入后话术预览展示在这里'),
                }}
              />
            </div>
          </section>
        </div>
        {showCopy && (
          <span>
            <CopyRegionButton targetRegion={region} onCopy={onCopy} />
          </span>
        )}
      </Container>
    </Element>
  );
}

const Container = styled.div`
  display: flex;
  align-items: stretch;
  position: relative;
  > section {
    flex: 1 0 0;
    overflow: hidden;
  }
  > div {
    flex: 0 0 auto;
    margin-left: 24px;
    padding-left: 24px;
    border-left: solid 1px var(--fett-border-default);
    > section {
      width: 320px;
      margin-top: 6px;
      border: solid 1px var(--fett-streak-grey);
      > header {
        height: 40px;
        line-height: 40px;
        padding: 0 16px;
      }
      > div {
        min-height: 320px;
        padding: 16px;
        background: var(--fett-erp-card-bg);
      }
    }
  }
  > span {
    position: absolute;
    top: -32px;
    right: 352px;
  }
`;

export default Region;
